import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Alert,
  Tabs,
  Tab,
  Grid,
  Snackbar,
  Button,
} from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InsightsIcon from '@mui/icons-material/Insights';
import { useWorkStore, useAuthStore, useMentorStore, useManagerQueueStore } from '../../stores/root_store';
import { RequestList } from '../../components/RequestList';

export const AssistantDashboard = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useAuthStore();
  const workStore = useWorkStore();
  const mentorStore = useMentorStore();
  const managerQueueStore = useManagerQueueStore();

  // Check if user is a mentor or manager
  const isMentor = authStore.isMentor;
  const isManager = authStore.roles.includes('manager');

  // Determine current tab from URL
  const getTabValue = () => {
    const path = location.pathname;
    if (path.endsWith('/mine')) return 'mine';
    if (path.endsWith('/mentor')) return 'mentor';
    if (path.endsWith('/manager')) return 'manager';
    if (path.endsWith('/closed')) return 'closed';
    return 'available'; // /assistant or /assistant/available
  };
  const tab = getTabValue();

  // Redirect non-assistants
  useEffect(() => {
    if (authStore.initialized && !authStore.isAssistant) {
      navigate('/request');
    }
  }, [authStore.initialized, authStore.isAssistant, navigate]);

  const isAssistant = authStore.isAssistant;

  // Fetch work lists for assistants
  useEffect(() => {
    if (isAssistant) {
      workStore.fetchAvailableRequests(true);
      workStore.fetchClaimedRequests(true);
    }
  }, [isAssistant, workStore]);

  // Fetch mentor queue if user is a mentor
  useEffect(() => {
    if (isAssistant && isMentor) {
      mentorStore.fetchMentorQueue(true);
    }
  }, [isAssistant, isMentor, mentorStore]);

  // Fetch manager queue if user is a manager
  useEffect(() => {
    if (isAssistant && isManager) {
      managerQueueStore.fetchQueue(true);
    }
  }, [isAssistant, isManager, managerQueueStore]);

  // Fetch closed requests only when the closed tab is active (lazy load)
  useEffect(() => {
    if (isAssistant && tab === 'closed') {
      workStore.fetchClosedRequests(true);
    }
  }, [isAssistant, tab, workStore]);

  // The task just hidden, so the snackbar can offer Undo
  const [hiddenKey, setHiddenKey] = useState<string | null>(null);

  const handleHideRequest = async (requestKey: string, reason: number) => {
    if (await workStore.declineRequest(requestKey, reason)) {
      setHiddenKey(requestKey);
    }
  };

  const handleUndoHide = async () => {
    const key = hiddenKey;
    setHiddenKey(null);
    if (key) await workStore.undoDeclineRequest(key);
  };

  const handleClaimRequest = async (requestKey: string) => {
    const request = await workStore.claimRequest(requestKey);
    if (request) {
      navigate(`/assistant/request/${request.key}`);
    }
  };

  if (!isAssistant) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          You do not have assistant access.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Main Content - Tabbed Interface */}
        <Grid size={{ xs: 12, md: 8 }}>
          {workStore.error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => workStore.clearError()}>
              {workStore.error}
            </Alert>
          )}

          {/* Request Lists */}
          <Card>
            <Tabs
              value={tab}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value="available"
                label={`Available (${workStore.availableRequests.length})`}
                component={Link}
                to="/assistant/available"
              />
              <Tab
                value="mine"
                label={`Mine (${workStore.claimedRequests.length})`}
                component={Link}
                to="/assistant/mine"
              />
              {isMentor && (
                <Tab
                  value="mentor"
                  label={`Mentor (${mentorStore.mentorQueue.filter(r => !r.mentor_claimed || r.mentor === authStore.user?.uuid).length})`}
                  component={Link}
                  to="/assistant/mentor"
                />
              )}
              {isManager && (
                <Tab
                  value="manager"
                  label={`Manager (${managerQueueStore.queue.length})`}
                  component={Link}
                  to="/assistant/manager"
                />
              )}
              <Tab
                value="closed"
                label="Closed"
                component={Link}
                to="/assistant/closed"
              />
            </Tabs>

            <CardContent>
              {tab === 'available' && (
                <RequestList
                  items={workStore.availableRequests}
                  loading={workStore.availableLoading}
                  emptyMessage="No available requests at the moment."
                  hasMore={workStore.availableHasMore}
                  onLoadMore={() => workStore.fetchAvailableRequests(false)}
                  variant="available"
                  onItemClaim={handleClaimRequest}
                  onItemHide={handleHideRequest}
                  onItemClick={(key) => navigate(`/assistant/request/${key}`)}
                  clickBehavior="expand"
                />
              )}

              {tab === 'mine' && (
                <RequestList
                  items={workStore.claimedRequests}
                  loading={workStore.claimedLoading}
                  emptyMessage="You haven't claimed any requests yet."
                  variant="claimed"
                  onItemClick={(key) => navigate(`/assistant/request/${key}`)}
                />
              )}

              {tab === 'mentor' && isMentor && (
                <RequestList
                  items={mentorStore.mentorQueue.filter(r => !r.mentor_claimed || r.mentor === authStore.user?.uuid)}
                  loading={mentorStore.mentorLoading}
                  emptyMessage="No tasks awaiting mentor approval."
                  hasMore={mentorStore.mentorHasMore}
                  onLoadMore={() => mentorStore.fetchMentorQueue(false)}
                  variant="mentor"
                  onItemClick={(key) => navigate(`/assistant/request/${key}`)}
                  error={mentorStore.error}
                  currentUserUuid={authStore.user?.uuid}
                />
              )}

              {tab === 'manager' && isManager && (
                <RequestList
                  items={managerQueueStore.queue}
                  loading={managerQueueStore.loading}
                  emptyMessage="No requests need manager attention."
                  hasMore={managerQueueStore.hasMore}
                  onLoadMore={() => managerQueueStore.fetchQueue(false)}
                  variant="manager"
                  onItemClick={(key) => navigate(`/assistant/request/${key}`)}
                  error={managerQueueStore.error}
                />
              )}

              {tab === 'closed' && (
                <RequestList
                  items={workStore.closedRequests}
                  loading={workStore.closedLoading}
                  emptyMessage="No closed requests yet."
                  hasMore={workStore.closedHasMore}
                  onLoadMore={() => workStore.fetchClosedRequests(false)}
                  variant="claimed"
                  onItemClick={(key) => navigate(`/assistant/request/${key}`)}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Field Guide Card */}
          <Card>
            <CardContent>
              <Box
                component={Link}
                to="/assistant/fieldguide"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <MenuBookIcon color="primary" />
                <Box>
                  <Typography variant="h6">Field Guide</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reference and training materials
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Forum Card */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box
                component={Link}
                to="/forum"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <ForumIcon color="primary" />
                <Box>
                  <Typography variant="h6">Forum</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Discuss with the team
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* My Stats Card */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box
                component={Link}
                to="/assistant/stats"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <InsightsIcon color="primary" />
                <Box>
                  <Typography variant="h6">My Stats</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ratings, tasks closed and mentor reviews
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={hiddenKey !== null}
        autoHideDuration={8000}
        onClose={(_, reason) => reason !== 'clickaway' && setHiddenKey(null)}
        message="Task hidden from your Available list"
        action={
          <Button color="secondary" size="small" onClick={handleUndoHide}>
            Undo
          </Button>
        }
      />
    </Container>
  );
});
